/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { Component, inject, input, OnInit, signal } from '@angular/core';
import { TeamDto, TournamentDto, TournamentEndpointService } from '@api';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { catchError, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationService } from '../../services/confirmation.service';
import { MatChipsModule, MatChipTrailingIcon } from '@angular/material/chips';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCard } from '@angular/material/card';
import { RouterLink, RouterModule } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TournamentDialogsService } from 'src/app/services/tournament-dialogs.service';
import { HeaderCardComponent } from '../header-card/header-card.component';

@Component({
  selector: 'app-tournament-teams',
  standalone: true,
  imports: [
    MatCard,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderCellDef,
    MatIcon,
    MatCellDef,
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    MatButton,
    MatIconButton,
    MatChipsModule,
    MatChipTrailingIcon,
    RouterModule,
    RouterLink,
    MatTooltip,
    HeaderCardComponent,
  ],
  templateUrl: './tournament-teams.component.html',
  styleUrl: './tournament-teams.component.scss',
})
export class TournamentTeamsComponent implements OnInit {
  tournamentService = inject(TournamentEndpointService);
  snackBar = inject(MatSnackBar);
  deleteConfirmationService = inject(ConfirmationService);
  private tournamentDialogs = inject(TournamentDialogsService);

  tournamentId = input<number>();
  tournament: TournamentDto | undefined = undefined;
  teams = signal<TeamDto[]>([]);
  displayedColumns: string[] = ['name', 'checkedIn', 'actions'];

  ngOnInit(): void {
    this.loadTeams();
    this.loadTournament();
  }

  loadTournament() {
    if (this.tournamentId() !== undefined) {
      this.tournamentService.get(this.tournamentId()!).subscribe({
        next: dto => (this.tournament = dto),
        error: error => {
          console.error(error);

          this.snackBar.open('Failed to load tournament: ' + error.message, 'Close');
        },
      });
    }
  }

  asTeamDto(team: TeamDto): TeamDto {
    return team;
  }

  loadTeams(): void {
    if (this.tournamentId() !== undefined) {
      this.tournamentService
        .getTournamentTeams(this.tournamentId()!)
        .pipe(
          catchError(error => {
            console.error(error);
            this.snackBar.open('Failed to load teams: ' + error.message, 'Close');
            return of([]);
          }),
        )
        .subscribe((teams: TeamDto[]) => {
          this.teams.set(teams);
        });
    }
  }

  async deleteTeam(team: TeamDto) {
    if (
      (await this.deleteConfirmationService.openConfirmationDialog(
        `deleting ${team.name!}`,
        undefined,
        true,
        'Delete',
      )) &&
      this.tournamentId() !== undefined
    ) {
      this.tournamentService
        .deleteTournamentTeam(this.tournamentId()!, team.id!)
        .pipe(
          catchError((e: HttpErrorResponse) => {
            console.error(e.error);
            this.snackBar.open('Failed to load teams: ' + e.error, 'Close');
            return [];
          }),
        )
        .subscribe(() => {
          this.loadTeams();
          this.snackBar.open(`Team '${team.name}' deleted.`, 'Close', { duration: 3000 });
        });
    }
  }

  async checkInTeam(team: TeamDto): Promise<void> {
    if (this.tournamentId() !== undefined && team.id !== undefined) {
      const answer = await this.deleteConfirmationService.openConfirmationDialog(
        `check in team ${team.name}?`,
        'This action cannot be undone.',
        false,
        'Check In',
      );

      if (answer) {
        this.tournamentService
          .markTeamAsReady(this.tournamentId()!, team.id)
          .pipe(
            catchError(error => {
              console.error(error);
              this.snackBar.open('Failed to check in team: ' + error.message, 'Close');
              return of([]);
            }),
          )
          .subscribe(() => {
            this.loadTeams();
          });
      }
    }
  }

  async openTeamEditDialog(team: TeamDto) {
    const wasUpdated = await this.tournamentDialogs.openTeamEditDialog(this.tournamentId()!, team);
    if (wasUpdated) {
      this.loadTeams();
      this.snackBar.open(`Team ${team.name} updated.`, 'Close', { duration: 3000 });
    }
  }
}
